<script setup lang="ts">
/**
 * Work's merged view (#1747, N8 of #1697): one list of every execution on the board (a workflow), most recently
 * active first, each opening in place into its runs (its steps). The old Executions board and Runs list keep their own
 * pages and paths; this one lives at `/work` beside them (CTO decision 1).
 *
 * It only reads what already exists: the execution board, with the same search and team filter the Executions page
 * sends, and one execution's detail when a row is opened (`WorkExecutionRow`). It is for browsing: closing, stopping
 * and reopening stay on the Executions pages (CTO decision 7). Built from the kit alone (CTO decision 9).
 */
import { RefreshCw } from '@lucide/vue'
import { EXECUTION_LANES } from '@zerohuman/shared'
import {
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhEmptyState,
    ZhPageTitle,
    ZhStatusDot,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { api, type ExecutionSummary, type Team } from '@/api'
import FirstRunHint from '@/components/FirstRunHint.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import WorkExecutionRow from '@/components/WorkExecutionRow.vue'
import { useListState } from '@/composables/useListState'
import { useLiveResource } from '@/composables/useLiveResource'

const search = ref('')
/** '' is every team. */
const teamId = ref('')
const teams = ref<Team[]>([])
/** False until the teams are read: a saved team is kept until then, and dropped if it is not among them. */
const teamsLoaded = ref(false)
/** #633: the search and team come back on the next visit, in place before the first board read. */
const list = useListState('work', {
    search: { ref: search },
    teamId: { ref: teamId, allowed: () => (teamsLoaded.value ? teams.value.map(team => team.id) : null) },
})
onMounted(async () => {
    try {
        teams.value = await api.teams()
        teamsLoaded.value = true
    } catch {
        teams.value = []
    }
})
/**
 * The kit Select cannot hold '' as a value, so "All teams" is this stand-in inside the control alone. It maps back
 * to '' at the query boundary, so the board read sends `teamId: ''` exactly as the Executions page does.
 */
const ALL_TEAMS = '__all-teams__'
const teamChoice = computed(() => teamId.value || ALL_TEAMS)
function chooseTeam(choice: unknown): void {
    teamId.value = choice === null || choice === undefined || choice === ALL_TEAMS ? '' : String(choice)
}
const query = computed(() => ({ search: search.value.trim(), teamId: teamId.value }))

/** The board's own pace on the Executions page: every three seconds, with the elapsed times ticking each second. */
const BOARD_READ_MS = 3000
const { data, error, loading, refreshing, now, visible, refresh } = useLiveResource(
    () => JSON.stringify(query.value),
    signal => api.executionBoard(query.value, signal),
    { everyMs: BOARD_READ_MS },
)

/**
 * Every lane in one list, no lane headings: the most recently active first. An execution that takes its next step is
 * active again, so it rises; sorted by when they started, a burst of pipelines begun together held the top of the list
 * for hours while every step since happened inside them, and the page read as stopped.
 */
const lastActive = (execution: ExecutionSummary) => new Date(execution.updatedAt || execution.startedAt).getTime()
const executions = computed<ExecutionSummary[]>(() => {
    const board = data.value
    if (!board) return []
    return EXECUTION_LANES.flatMap(lane => board.lanes[lane]).sort((a, b) => lastActive(b) - lastActive(a))
})
const total = computed(() => {
    const board = data.value
    return board ? EXECUTION_LANES.reduce((sum, lane) => sum + board.totals[lane], 0) : 0
})
/** The board sends only the newest completed executions; the list says so rather than letting the count disagree. */
const completeCapped = computed(() => {
    const board = data.value
    return board && board.totals.complete > board.lanes.complete.length
        ? { shown: board.lanes.complete.length, of: board.totals.complete }
        : null
})
</script>

<template>
    <section class="w-full min-w-0 space-y-6 p-4 md:p-8">
        <header class="flex flex-wrap items-start justify-between gap-4">
            <div>
                <ZhPageTitle>Workflows</ZhPageTitle>
                <p class="text-muted-foreground mt-1 max-w-2xl text-sm">Every workflow in one list. Open one to see its runs, step by step.</p>
            </div>
            <div class="flex items-center gap-3">
                <span class="text-muted-foreground flex items-center gap-2 text-xs"><ZhStatusDot :state="error ? 'active' : visible ? 'complete' : 'inactive'" :pulse="visible" />{{ error ? 'Reconnecting…' : visible ? 'Live' : 'Updates paused' }}</span>
                <Button variant="outline" size="icon-sm" aria-label="Refresh workflows" :disabled="refreshing" @click="refresh"><RefreshCw :size="15" /></Button>
            </div>
        </header>
        <div v-if="error" role="alert" class="text-destructive flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{{ data ? 'Updates interrupted. Showing the last received executions.' : 'Could not load executions.' }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing" @click="refresh">Try again</Button>
        </div>
        <div class="flex flex-wrap items-center gap-3">
            <ListSearchField v-model="search" type="search" aria-label="Search executions" placeholder="Search tasks, teams, members…" class="flex-1 sm:max-w-sm" />
            <!-- `aria-label` lands on the field the kit Select mirrors its choice on; the trigger carries it for people. -->
            <Select :model-value="teamChoice" aria-label="Filter by team" @update:model-value="chooseTeam">
                <SelectTrigger aria-label="Filter by team">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem :value="ALL_TEAMS">All teams</SelectItem>
                    <SelectItem v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</SelectItem>
                </SelectContent>
            </Select>
            <Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="list.clear()">Clear filters</Button>
            <span v-if="data" class="text-muted-foreground ml-auto text-xs tabular-nums">{{ total }} execution{{ total === 1 ? '' : 's' }}</span>
        </div>
        <p v-if="loading && !data" role="status" class="text-muted-foreground py-12 text-center text-sm">Loading executions…</p>
        <FirstRunHint
            v-else-if="data && total === 0 && !search && !teamId"
            lead="No executions yet. Runs of your tasks appear here, grouped into executions."
            :action="{ label: 'Go to Tasks', to: '/tasks' }"
        />
        <ZhEmptyState v-else-if="data && total === 0" label="No matching executions">Try another search or team.</ZhEmptyState>
        <div v-else-if="data" class="space-y-3">
            <WorkExecutionRow v-for="execution in executions" :key="execution.id" :execution="execution" :now="now" />
            <p v-if="completeCapped" class="text-muted-foreground text-center text-xs tabular-nums">Showing the {{ completeCapped.shown }} most recent of {{ completeCapped.of }} completed</p>
        </div>
    </section>
</template>
